export type Parameter = {
    readonly name: string;
    readonly type: string;
    readonly required?: boolean;
    readonly description: string;
};

export type Endpoint = {
    readonly id: string;
    readonly title: string;
    readonly method: 'GET' | 'POST';
    readonly path: string;
    readonly description: string;
    readonly parameters?: readonly Parameter[];
    readonly request?: string;
    readonly response?: string;
    readonly responseExamples?: readonly {
        readonly title: string;
        readonly content: string;
    }[];
    readonly responseParameters?: readonly Parameter[];
    readonly statusCodes?: readonly (readonly [code: string, description: string])[];
    readonly note?: string;
};

export type PaymentMethodInfo = {
    readonly name: string;
    readonly tag: string;
    readonly countries: readonly string[];
    readonly currencies: readonly string[];
};

export const paymentMethodCatalog: readonly PaymentMethodInfo[] = [
    { name: 'Bancontact', tag: 'bancontact', countries: ['BE'], currencies: ['EUR'] },
    { name: 'Bizum', tag: 'bizum', countries: ['ES'], currencies: ['EUR'] },
    { name: 'BLIK', tag: 'blik', countries: ['PL'], currencies: ['PLN'] },
    {
        name: 'Cards',
        tag: 'card',
        countries: ['AT', 'BE', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'IT', 'NL', 'NO', 'PL', 'PT', 'RO', 'ES', 'SE', 'CH', 'GB'],
        currencies: ['EUR', 'GBP', 'AUD', 'DKK', 'NOK', 'PLN', 'SEK', 'CHF']
    },
    { name: 'EPS', tag: 'eps', countries: ['AT'], currencies: ['EUR'] },
    { name: 'Giropay', tag: 'giropay', countries: ['DE'], currencies: ['EUR'] },
    { name: 'iDEAL', tag: 'ideal', countries: ['NL'], currencies: ['EUR'] },
    { name: 'Klarna Pay Now', tag: 'klarnapaynow', countries: ['DE'], currencies: ['EUR'] },
    { name: 'Multibanco', tag: 'multibanco', countries: ['PT'], currencies: ['EUR'] },
    { name: 'MBWay', tag: 'mbway', countries: ['PT'], currencies: ['EUR'] },
    { name: 'MobilePay', tag: 'mobilepay', countries: ['DK', 'FI'], currencies: ['EUR'] },
    {
        name: 'Neteller',
        tag: 'neteller',
        countries: ['AT', 'BE', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
        currencies: ['EUR', 'DKK', 'GBP', 'NOK', 'PLN', 'SEK', 'RON']
    },
    {
        name: 'Open Banking',
        tag: 'ob',
        countries: ['SE', 'FI', 'NL', 'DE', 'GB'],
        currencies: ['SEK', 'EUR', 'GBP']
    },
    {
        name: 'Open Banking',
        tag: 'op',
        countries: ['FI', 'IT', 'NL', 'NO', 'PT', 'SE'],
        currencies: ['EUR', 'SEK', 'NOK']
    },
    { name: 'PayID', tag: 'payid', countries: ['AU'], currencies: ['AUD'] },
    { name: 'Paysafecard', tag: 'paysafecard', countries: ['DE', 'FR', 'ES', 'IT', 'AT', 'NL', 'PL', 'SE', 'NO', 'DK', 'BE', 'CZ', 'PT', 'IE'], currencies: ['EUR'] },
    { name: 'Paysafecash', tag: 'paysafecash', countries: ['DE', 'FR', 'ES', 'IT', 'AT', 'NL', 'PL', 'SE', 'NO', 'DK', 'BE', 'CZ', 'PT', 'IE'], currencies: ['EUR'] },
    { name: 'PIX', tag: 'pix', countries: ['BR'], currencies: ['BRL'] },
    { name: 'Przelewy24', tag: 'p24', countries: ['PL'], currencies: ['PLN', 'EUR'] },
    { name: 'RapidTransfer', tag: 'rapidtransfer', countries: ['DE', 'FR', 'ES', 'IT', 'AT', 'NL', 'PL', 'SE', 'NO', 'DK', 'BE', 'CZ', 'PT', 'IE'], currencies: ['EUR'] },
    { name: 'Safetypay', tag: 'safetypay', countries: ['AT', 'BE', 'BR', 'CL', 'DE', 'EC', 'MX', 'NL', 'PE', 'ES'], currencies: ['USD', 'EUR', 'MXN'] },
    { name: 'Sepa Transfer', tag: 'sepatransfer', countries: ['AT', 'BE', 'FR', 'IT', 'LT', 'LU', 'MT'], currencies: ['EUR'] },
    {
        name: 'Skrill',
        tag: 'skrill',
        countries: ['AT', 'BE', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
        currencies: ['EUR', 'DKK', 'GBP', 'NOK', 'PLN', 'SEK', 'RON']
    },
    { name: 'SPEI', tag: 'spei', countries: ['MX'], currencies: ['MXN'] },
    { name: 'Swish', tag: 'swish', countries: ['SE'], currencies: ['SEK'] },
    { name: 'Trustly', tag: 'trustly', countries: ['AT', 'DK', 'EE', 'FI', 'DE', 'LV', 'LT', 'NL', 'NO', 'PL', 'ES', 'SE', 'GB'], currencies: ['EUR', 'DKK', 'GBP', 'NOK', 'PLN', 'SEK'] },
    { name: 'Vipps', tag: 'vipps', countries: ['NO'], currencies: ['NOK'] },
    { name: 'Wero', tag: 'wero', countries: ['DE'], currencies: ['EUR'] }
];

export const transactionParameters: readonly Parameter[] = [
    { name: 'api_id', type: 'Long', required: true, description: 'Merchant API identifier provided by Macropay. Send it in the request body or as the api_id header.' },
    { name: 'api_guid', type: 'String', required: true, description: 'Merchant API secret provided by Macropay. Send it in the request body or as the api_secret header.' },
    { name: 'channel', type: 'String', required: true, description: 'Use "testchannel" for testing and "livechannel" for production.' },
    { name: 'tag', type: 'String', required: true, description: 'Exact payment method tag returned by the payment-methods endpoint.' },
    { name: 'currency', type: 'String', required: true, description: 'ISO 4217 transaction currency.' },
    { name: 'amount', type: 'Decimal', required: true, description: 'Transaction amount, using a dot and no more than two decimals.' },
    { name: 'countrycode', type: 'String', required: true, description: 'ISO country code for the country where the transaction takes place.' },
    { name: 'trackingmembercode', type: 'String', required: true, description: 'Merchant order or tracking reference. It must be unique for 24 hours.' },
    { name: 'transaction_count', type: 'Integer', required: false, description: 'Player transaction count known by the merchant at the time of the request.' },
    { name: 'merchantredirecturl', type: 'URL', required: true, description: 'Destination after the consumer completes or abandons the payment.' },
    { name: 'notificationurl', type: 'URL', required: true, description: 'Endpoint that receives transaction status notifications.' },
    { name: 'source_url', type: 'URL', required: true, description: 'Exact originating merchant website.' },
    { name: 'source_ip', type: 'IP address', required: true, description: 'Actual IP address of the end user.' },
    { name: 'customer', type: 'Object', required: true, description: 'JSON object containing the customer fields listed below. Do not send these fields as dot-separated top-level properties.' },
    { name: 'customer.first_name', type: 'String', required: true, description: 'Customer first name, between 3 and 32 characters.' },
    { name: 'customer.last_name', type: 'String', required: true, description: 'Customer last name, between 3 and 64 characters.' },
    { name: 'customer.email', type: 'Email', required: true, description: 'Valid customer email address.' },
    { name: 'customer.phone', type: 'String', required: true, description: 'Customer phone including country code; 5 to 20 characters, without special characters.' },
    { name: 'customer.phone_verified', type: 'Integer', required: false, description: 'Whether the phone is verified. Allowed values: 0 or 1.' },
    { name: 'customer.address', type: 'String', required: false, description: 'Customer address, up to 128 characters, without special characters.' },
    { name: 'customer.zip', type: 'String', required: false, description: 'Customer postal code, up to 20 characters, without special characters.' },
    { name: 'customer.city', type: 'String', required: false, description: 'Customer city, up to 128 characters, without special characters.' },
    { name: 'customer.country', type: 'String', required: false, description: 'ISO 3166 numeric country code.' },
    { name: 'specin.*', type: 'Varies', required: false, description: 'Payment-method-specific top-level fields such as specin.bic (iDEAL: valid BIC of the consumer\'s bank, 8 or 11 alphanumeric characters). Unlike customer, these keys are dot-separated field names. Up to 29 specin parameters can be sent per request; the fields each method requires are returned by the payment-methods endpoint.' },
];

export const processorEndpoints: readonly Endpoint[] = [
    {
        id: 'create-transaction',
        title: 'Create a transaction',
        method: 'POST',
        path: '/v1/transaction',
        description: 'Create a payment and receive the URL used to redirect the consumer to the selected payment method.',
        parameters: transactionParameters,
        request: `{
  "api_id": 117195987,
  "api_guid": "YOUR-API-GUID",
  "channel": "testchannel",
  "tag": "giropay",
  "source_ip": "172.217.17.110",
  "source_url": "https://www.merchantsite.com",
  "currency": "EUR",
  "amount": 15.00,
  "countrycode": "DE",
  "trackingmembercode": "order-010",
  "transaction_count": 12,
  "merchantredirecturl": "https://merchant.com/payment-result",
  "notificationurl": "https://merchant.com/notifications",
  "customer": {
    "first_name": "John",
    "last_name": "Doe",
    "email": "john.doe@example.com",
    "phone": "+49123456789"
  }
}`,
        response: `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultMessage": "PENDING",
  "ResultTransactionId": "1824169550",
  "ResultTransactionGuid": "80290ef1-ee35-4186-93dd-e222fc503eba",
  "ResultTrackingMemberCode": "order-010",
  "ResultCdcData": {
    "REDIRECTURL": "https://checkout.example/continue/...",
    "TXID": "GMJ2KVJNQ5PFWR82",
    "ERRMSG": "",
    "FLAGS": "",
    "TAG": "giropay",
    "STATUS": "PENDING",
    "CHANNEL": "testchannel"
  }
}`,
        responseParameters: [
            { name: 'ResultState', type: 'Boolean', description: 'true when the request was processed successfully; any other value is an error.' },
            { name: 'ResultCode', type: 'Integer', description: '0 when the request was processed successfully; any other value is an error.' },
            { name: 'ResultMessage', type: 'String', description: 'Readable result of the request, for example PENDING.' },
            { name: 'ResultTransactionId', type: 'String', description: 'Macropay transaction identifier. Store it to query the status and to match notifications (txid).' },
            { name: 'ResultTransactionGuid', type: 'UUID', description: 'Macropay transaction GUID. Store it together with ResultTransactionId (txguid in notifications).' },
            { name: 'ResultTrackingMemberCode', type: 'String', description: 'The trackingmembercode sent in the request, echoed back.' },
            { name: 'ResultCdcData.REDIRECTURL', type: 'URL', description: 'Single-use URL where the consumer completes the payment.' },
            { name: 'ResultCdcData.TXID', type: 'String', description: 'Payment request identifier. Sent back as PaReqId on the webhook redirect.' },
            { name: 'ResultCdcData.ERRMSG', type: 'String', description: 'Error message from the payment method, empty when there is no error.' },
            { name: 'ResultCdcData.FLAGS', type: 'String', description: 'Processing flags, usually empty.' },
            { name: 'ResultCdcData.TAG', type: 'String', description: 'Payment method tag used for the transaction.' },
            { name: 'ResultCdcData.STATUS', type: 'String', description: 'Current transaction status.' },
            { name: 'ResultCdcData.CHANNEL', type: 'String', description: 'testchannel or livechannel.' }
        ],
        note: 'Credentials may be sent in the request body as api_id and api_guid, or in headers as api_id and api_secret. Required: include source_url with the exact originating website for every transaction request. The redirect URL returned by Macropay is single-use; do not validate it with a GET request before sending the consumer to it.'
    },
    {
        id: 'transaction-status',
        title: 'Get transaction status',
        method: 'POST',
        path: '/v1/getTransactionStatus',
        description: 'Retrieve the latest transaction, funds and settlement status.',
        parameters: [
            { name: 'api_id', type: 'Long', required: true, description: 'Merchant API identifier. Send it in the request body or as the api_id header.' },
            { name: 'api_guid', type: 'String', required: true, description: 'Merchant API secret. Send it in the request body or as the api_secret header.' },
            { name: 'transaction_id', type: 'String', required: true, description: 'Transaction identifier returned when the payment was created.' },
            { name: 'transaction_guid', type: 'UUID', required: true, description: 'Transaction GUID returned when the payment was created.' }
        ],
        request: `{
  "api_id": 117195987,
  "api_guid": "YOUR-API-GUID",
  "transaction_id": "2483750937",
  "transaction_guid": "ce7ad01d-6842-4788-9e97-168b4dc2056f"
}`,
        responseExamples: [
            {
                title: 'Response without decision',
                content: `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultCdcData": {
    "transaction_id": "2483750937",
    "transaction_guid": "ce7ad01d-6842-4788-9e97-168b4dc2056f",
    "tracking_member_code": "order-010",
    "amount": "10.000",
    "currency": "EUR",
    "tag": "ideal",
    "channel": "testchannel",
    "status": "PENDING",
    "fundsstatus": null,
    "settlement_fundsstatus": null,
    "customer": {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com"
    },
    "partials": []
  },
  "ResultDecision": [],
  "ResultReason": ""
}`
            },
            {
                title: 'Response with decision',
                content: `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultCdcData": {
    "transaction_id": "2396543143",
    "transaction_guid": "2ba4d857-b4e9-48de-90c2-62b88c732593",
    "tracking_member_code": "order-011",
    "amount": "10.000",
    "currency": "EUR",
    "tag": "ideal",
    "channel": "testchannel",
    "status": "SUCCEEDED",
    "fundsstatus": "WAITING",
    "settlement_fundsstatus": null,
    "customer": {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com"
    },
    "partials": []
  },
  "ResultDecision": {
    "ResultCode": 0,
    "ResultState": true,
    "ResultMessage": "SUCCEEDED",
    "ResultTransactionId": "2396543143",
    "ResultTransactionGuid": "2ba4d857-b4e9-48de-90c2-62b88c732593",
    "ResultTrackingMemberCode": "order-011",
    "ResultCdcData": {
      "TXID": "2396543143",
      "FLAGS": "C",
      "ERRMSG": "",
      "STATE_NAME": "PAID"
    }
  },
  "ResultReason": ""
}`
            },
            {
                title: 'Failed response with reason',
                content: `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultCdcData": {
    "transaction_id": "2396543143",
    "transaction_guid": "2ba4d857-b4e9-48de-90c2-62b88c732593",
    "tracking_member_code": "order-012",
    "amount": "10.000",
    "currency": "EUR",
    "tag": "ideal",
    "channel": "testchannel",
    "status": "FAILED",
    "fundsstatus": null,
    "settlement_fundsstatus": null,
    "customer": {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com"
    },
    "partials": []
  },
  "ResultDecision": {
    "ResultCdcData": {
      "ERRMSG": "Payment declined",
      "DESCRIPTION": "Payment could not be completed",
      "STATE_NAME": "DECLINED"
    }
  },
  "ResultReason": "PAYMENT DECLINED; PAYMENT COULD NOT BE COMPLETED; DECLINED"
}`
            }
        ],
        note: 'Credentials may be sent in the request body as api_id and api_guid, or in headers as api_id and api_secret. ResultReason is empty unless the transaction status is FAILED or REJECT. For failed or rejected transactions it contains a semicolon-separated, uppercase list of unique failure details collected from ResultDecision (ERRMSG, DESCRIPTION and STATE_NAME) and the processor response (ERRMSG). Banking SDK transactions without a selected bank also include NO BANK SELECTED; processor DATA keys containing "error" add ERROR.'
    },
    {
        id: 'payment-methods',
        title: 'List available payment methods',
        method: 'GET',
        path: '/v1/payment-methods',
        description: 'Returns the payment methods enabled for the merchant together with their countries, currencies and method-specific fields.',
        responseExamples: [
            {
                title: 'Payment method without specific fields',
                content: `{
  "tag": "ideal",
  "countriesAvailable": "NL",
  "currenciesAvailable": "EUR,PLN",
  "requireSpecIn": 0,
  "specInParameters": []
}`
            },
            {
                title: 'Payment method with a specific field',
                content: `{
  "tag": "example",
  "countriesAvailable": "DE",
  "currenciesAvailable": "EUR",
  "requireSpecIn": 1,
  "specInParameters": [
    {
      "methodSpecInField": "specin.bic",
      "methodSpecInFieldIsMandatory": 1,
      "methodSpecInFieldType": "text",
      "methodSpecInFieldDescription": "BIC of the consumer's bank",
      "methodSpecInFieldOptions": null
    }
  ]
}`
            }
        ],
        note: 'The live list for the signed-in merchant is available in the Payment methods section below.'
    },
    {
        id: 'get-consumer',
        title: 'Get consumer',
        method: 'POST',
        path: '/v1/check-consumer',
        description: 'Retrieve whitelist, blacklist and KYC information for a consumer email address.',
        parameters: [
            { name: 'email', type: 'Email', required: true, description: 'Consumer email address.' }
        ],
        request: `{
  "email": "consumer@example.com"
}`,
        response: `{
  "email": "consumer@example.com",
  "hash": "hashed-email",
  "isWhitelisted": true,
  "isBlacklisted": false,
  "hasKyc": true
}`,
        statusCodes: [
            ['200', 'Consumer found. The body contains the consumer details shown above.'],
            ['404', 'Consumer not found. The body is empty.'],
            ['400', 'Invalid credentials.']
        ]
    },
    {
        id: 'consumer-kyc',
        title: 'Consumer whitelist and KYC',
        method: 'POST',
        path: '/v1/consumer-kycs',
        description: 'Whitelist a consumer and securely upload the supporting KYC document.',
        parameters: [
            { name: 'file', type: 'File', required: true, description: 'PDF, JPG, JPEG, PNG, RAR or ZIP, up to 50 MB.' },
            { name: 'verified_first_name', type: 'String', required: true, description: 'Verified first name.' },
            { name: 'verified_last_name', type: 'String', required: true, description: 'Verified last name.' },
            { name: 'verified_dob', type: 'Date', required: true, description: 'YYYY-MM-DD; consumer must be at least 18.' },
            { name: 'address', type: 'String', required: true, description: 'Verified address, up to 128 characters.' },
            { name: 'verified_nationality', type: 'String', required: true, description: 'Uppercase ISO alpha-2 nationality.' },
            { name: 'phone', type: 'String', required: true, description: 'Verified phone number, between 5 and 20 characters.' },
            { name: 'email', type: 'Email', required: true, description: 'Consumer email address.' }
        ],
        request: `curl --request POST "https://testapi.payments-gateway.co/v1/consumer-kycs" \\
  --header "auth-id: 12344" \\
  --header "auth-secret: YOUR-API-GUID" \\
  --header "Accept: application/json" \\
  --form "file=@passport.pdf" \\
  --form "verified_first_name=John" \\
  --form "verified_last_name=Doe" \\
  --form "verified_dob=1985-10-10" \\
  --form "address=123 Main Street" \\
  --form "verified_nationality=US" \\
  --form "phone=+123456789" \\
  --form "email=consumer@example.com"`,
        responseExamples: [
            {
                title: 'Created (201)',
                content: `{
  "verified_first_name": "John",
  "verified_last_name": "Doe",
  "verified_dob": "1985-10-10",
  "address": "123 Main Street",
  "verified_nationality": "US",
  "phone": "+123456789",
  "email": "consumer@example.com"
}`
            },
            {
                title: 'Not found (404)',
                content: `{
  "message": "..."
}`
            }
        ],
        statusCodes: [
            ['201', 'KYC submitted. The body echoes the verified consumer details.'],
            ['404', 'Not found. The body contains a message describing the problem.'],
            ['400', 'Invalid credentials.']
        ]
    }
];

const whitelistImportColumns = ['coreId', 'fileKey', 'originalFileName', 'merchantId', 'consumersImported', 'consumersUpdated', 'totalRows', 'isQueued', 'createdAt', 'updatedAt', 'consumersIgnored'];

const whitelistListResponse = `{
  "success": true,
  "code": 200,
  "locale": "en",
  "message": "success",
  "data": {
    "current_page": 1,
    "from": 1,
    "last_page": 1,
    "per_page": 10,
    "to": 1,
    "total": 1,
    "data": [
      {
        "coreId": 61,
        "fileKey": "..._2022_11_07_14_03_18.csv",
        "originalFileName": "example.csv",
        "consumersImported": 1,
        "consumersUpdated": 0,
        "totalRows": 1,
        "isQueued": 1,
        "createdAt": "2022-11-07 14:03:20",
        "updatedAt": null,
        "consumersIgnored": 0,
        "isProcessed": 1
      }
    ],
    "filterable": ${JSON.stringify(whitelistImportColumns)},
    "sortable": ${JSON.stringify(whitelistImportColumns)},
    "relations": []
  }
}`;

const whitelistImportResponse = `{
  "success": true,
  "code": 200,
  "locale": "en",
  "message": "success",
  "data": {
    "coreId": 61,
    "fileKey": "..._2022_11_07_14_03_18.csv",
    "originalFileName": "example.csv",
    "consumersImported": 0,
    "consumersUpdated": 0,
    "totalRows": 0,
    "isQueued": 0,
    "createdAt": "2022-11-07 14:03:20",
    "updatedAt": null,
    "consumersIgnored": 0,
    "isProcessed": 0
  }
}`;

export const whitelistTemplateColumns = '"First name","Last name","Email or hashed email","Phone number","Transaction Count","Nationality (iso2)","Date of birth (YYYY-MM-DD)","Address","Zip","City","Country (iso2 or iso3)"';
export const whitelistTemplateWithoutSddColumns = '"First name","Last name","Email or hashed email","Phone number","Transaction Count"';

export const whitelistEndpoints: readonly Endpoint[] = [
    {
        id: 'whitelist-list',
        title: 'List whitelist imports',
        method: 'POST',
        path: '/merchant/api/emails-whitelist/l/i/s/t',
        description: 'List and filter the merchant whitelist import history.',
        parameters: [
            { name: 'fileKey', type: 'String', required: false, description: 'Filter imports by file key. Send as fileKey=value in the request body.' },
            { name: 'coreId[in][]', type: 'Integer[]', required: false, description: 'Filter by one or more core IDs. Repeat coreId[in][]=value in the request body.' }
        ],
        responseExamples: [{ title: 'Success response (200)', content: whitelistListResponse }],
        note: 'isProcessed is 1 once the hourly import has run. Any column listed under filterable or sortable can be used with the list request parameters described in the Reporting and Payout API section.'
    },
    {
        id: 'whitelist-template',
        title: 'Download whitelist template',
        method: 'GET',
        path: '/merchant/api/template-emails-whitelist',
        description: 'Download the EDD whitelist CSV template. The response is a streamed CSV file containing the header row below. The Transaction Count column is optional.',
        responseExamples: [{ title: 'Template header row', content: whitelistTemplateColumns }]
    },
    {
        id: 'whitelist-template-no-sdd',
        title: 'Download simplified template',
        method: 'GET',
        path: '/merchant/api/template-emails-whitelist-without-sdd',
        description: 'Download the simplified whitelist CSV template without SDD fields. The response is a streamed CSV file containing the header row below. The Transaction Count column is optional.',
        responseExamples: [{ title: 'Template header row', content: whitelistTemplateWithoutSddColumns }]
    },
    {
        id: 'whitelist-import',
        title: 'Upload/import whitelist',
        method: 'POST',
        path: '/merchant/api/emails-whitelist',
        description: 'Import or upload a completed CSV file with whitelisted consumers via API.',
        parameters: [{ name: 'file', type: 'File', required: true, description: 'CSV file, for example example.csv, sent as multipart/form-data.' }],
        request: `curl --request POST "https://testapi.bo.payments-gateway.co/merchant/api/emails-whitelist" \\
  --header "auth-id: 12344" \\
  --header "auth-secret: YOUR-API-GUID" \\
  --header "Accept: application/json" \\
  --form "file=@example.csv"`,
        responseExamples: [{ title: 'Success response (200)', content: whitelistImportResponse }],
        note: 'Required headers: auth-id (API ID) and auth-secret (API GUID). The upload must use Content-Type: multipart/form-data. The counters stay at 0 until the file is processed at the next full hour; check progress with List whitelist imports.'
    }
];

const coreIdParameter: readonly Parameter[] = [
    { name: 'coreId', type: 'Integer', required: true, description: 'Resource identifier inserted in the URL path.' }
];

const filePathParameters: readonly Parameter[] = [
    ...coreIdParameter,
    { name: 'key', type: 'String', required: true, description: 'File key returned by the corresponding payout list endpoint.' }
];

export const listFilterSyntax = [
    ['Starts with', 'column=value'],
    ['In', 'column[in][]=value1&column[in][]=value2'],
    ['Not in', 'column[notIn][]=value'],
    ['From (inclusive)', 'column[from]=10'],
    ['To (inclusive)', 'column[to]=20'],
    ['Is null', 'column[o]=isNull'],
    ['Is null or one of', 'column[o]=isNull&column[v][]=5'],
    ['Is not null', 'column[o]=isNotNull'],
    ['Contains', 'column[o]=contains&column[v]=value'],
    ['Does not contain', 'column[o]=notContains&column[v]=value'],
    ['Sort', 'sort[0][by]=column&sort[0][dir]=DESC'],
    ['Select response columns', 'c=column1,column2,column3']
] as const;

export const listPaginationSyntax = [
    ['With count (default)', 'perPage=10', 'Returns total, last_page and the rows. Default page size is 10.'],
    ['Simple', 'simplePaginate=1&perPage=10', 'Skips the total count; use has_more_pages to detect the next page. Faster on large data sets.'],
    ['Cursor', 'cursor=1&perPage=10', 'Cursor-based paging for stable iteration over large result sets.'],
    ['Only count', 'perPage=0', 'Returns only the total number of matching rows.']
] as const;

type ListResponseOptions = {
    readonly rows: readonly Record<string, unknown>[];
    readonly filterable?: readonly string[];
    readonly sortable?: readonly string[];
    readonly aggregates?: Readonly<Record<string, unknown>>;
};

const listResponse = ({ rows, filterable, sortable, aggregates }: ListResponseOptions) => JSON.stringify({
    success: true,
    code: 200,
    locale: 'en',
    message: 'success',
    data: {
        has_more_pages: false,
        ...aggregates,
        current_page: 1,
        data: rows,
        from: 1,
        last_page: 1,
        per_page: 10,
        to: rows.length,
        total: rows.length,
        ...(filterable && { filterable }),
        ...(sortable && { sortable }),
        relations: []
    }
}, null, 2);

const emptyAggregates = { sums: null, avgs: null, mins: null, maxs: null };

const csvNotFoundResponse = JSON.stringify({ success: false, code: 400, locale: 'en', message: 'Not found', data: null }, null, 2);

const csvExample = (rows: readonly (readonly (string | null)[])[]) =>
    rows.map(row => row.map(value => (value === null ? '' : `"${value}"`)).join(',')).join('\n');

const csvBaseColumns = ['Transaction ID', 'Ref. ID', 'Client name', 'Consumer Email', 'Payment Method', 'Amount', 'Currency', 'Status', 'Funds Status', 'Tracking Member Code', 'Date Created', 'Date Updated', 'Consumer First Name', 'Consumer Last Name', 'Consumer Hashed Email', 'Type', 'Source Country', 'Sheet Ref.', 'Merchant Website'];
const csvFeeColumns = ['Conversion Rate', 'Converted Amount', 'MDR', 'Transaction Fee'];

const csvTransactionsExample = csvExample([
    [...csvBaseColumns, ...csvFeeColumns, 'Refund Fee', 'Settlement Currency'],
    ['...', '...', '...', 'mar************@g****.com', 'directpay', '3.00', 'EUR', 'REFUNDED', 'REFUNDED', '...', '2022-09-16 12:19:21', '2022-09-16 12:20:16', 'Mario', 'Test', '...', 'APM', 'DE', 'Refunds prior settled', null, '1.0000000000', '3.00', '0.03', '2.00', '50.00', 'EUR']
]);

const csvConsumerPayoutsExample = csvExample([
    [...csvBaseColumns, ...csvFeeColumns, 'Settlement Currency'],
    ['...', '...', 'merchant', 'consumer@example.com', 'directpay', '-25.00', 'EUR', 'CHARGE_BACK', 'SENT', 'Chargeback_...', '2024-10-31 14:24:48', null, 'John', 'Doe', '...', 'APM', 'DE', 'Partial chargebacks', null, '1.0000000000', '-25.00', '0.03', '2.00', 'EUR']
]);

const csvPendingSettlementExample = csvExample([
    csvBaseColumns,
    ['...', '...', '...', 'mar************@g****.com', 'directpay', '3.00', 'EUR', 'SUCCEEDED', null, '...', '2022-09-16 12:19:21', '2022-09-16 12:20:16', 'Mario', 'Test', '...', 'APM', 'DE', 'Waiting for settlement', null]
]);

const csvResponseExamples = (example: string) => [
    { title: 'Success (200): streamed CSV', content: example },
    { title: 'File not found (400)', content: csvNotFoundResponse }
];

const transactionListResponse = listResponse({
    aggregates: {
        sums: { amount: null, amountEur: null },
        avgs: { amount_avg: null, amountEur_avg: null },
        mins: { amount_min: null, amountEur_min: null },
        maxs: { amount_max: null, amountEur_max: null }
    },
    rows: [{
        coreId: 42763,
        transactionId: '...',
        transactionGuid: '...',
        countryIso2: 'NL',
        trackingMemberCode: 'order-010',
        amount: '5.00',
        amountEur: '5.00',
        redirectUrl: 'https://merchant.com/payment-result',
        notificationUrl: 'https://merchant.com/notifications',
        direction: 'C',
        currency: 'EUR',
        status: 'SUCCEEDED',
        fundStatus: 'WAITING',
        createdAt: '2025-09-12 10:34:01',
        updatedAt: '2025-09-12 10:35:06',
        consumerId: 522396,
        merchantId: 150,
        settlementStatus: null,
        settlementStatusDateTime: null,
        settledDate: null,
        isSettled: 0,
        localTime: '2025-09-12 10:34:21',
        transactionRequest: '...',
        transactionResponse: '...',
        transactionDecision: '...',
        consumerFirstName: 'John',
        consumerLastName: 'Doe',
        hashedEmail: '...',
        paymentMethodName: 'iDEAL',
        methodTag: 'ideal',
        email: '...',
        consumerIban: '',
        sourceUrl: null,
        businessName: null,
        cardIssuer: null,
        reason: '',
        requestSourceUrl: 'https://www.merchantsite.com',
        isPaid: 0,
        bankNotFound: null,
        obStatus: null,
        ibanBankName: '',
        refundableAmount: '5.00',
        isEcommerce: 1,
        reasonCode: '',
        children: [{
            childTransactionId: '...',
            childAmount: '2.00',
            childCurrency: 'EUR',
            childCreatedAt: '2025-09-13 08:10:00',
            childUpdatedAt: null,
            childStatus: 'REFUNDED',
            childFundsStatus: 'REFUNDED',
            childDirection: 'D',
            childReason: '',
            childReasonCode: ''
        }],
        payIdAlias: null,
        pki: 42763
    }],
    filterable: ['coreId', 'transactionId', 'transactionGuid', 'countryIso2', 'trackingMemberCode', 'amount', 'amountEur', 'redirectUrl', 'notificationUrl', 'direction', 'currency', 'status', 'createdAt', 'updatedAt', 'consumerId', 'merchantId', 'settlementStatus', 'settlementStatusDateTime', 'settledDate', 'isSettled', 'localTime', 'transactionRequest', 'transactionResponse', 'transactionDecision', 'consumerFirstName', 'consumerLastName', 'hashedEmail', 'paymentMethodName', 'methodTag', 'isPaid', 'email', 'consumerIban', 'sourceUrl', 'businessName', 'cardIssuer', 'payIdAlias'],
    sortable: ['coreId', 'transactionId', 'trackingMemberCode', 'direction', 'status', 'createdAt', 'updatedAt', 'consumerId', 'merchantId', 'settlementStatus']
});

const payoutTotalsAggregates = {
    sums: { amountPaid: null, totalNet: null, totalGross: null, '...': null },
    avgs: { amountPaid_avg: null, totalNet_avg: null, totalGross_avg: null, '...': null },
    mins: { amountPaid_min: null, totalNet_min: null, totalGross_min: null, '...': null },
    maxs: { amountPaid_max: null, totalNet_max: null, totalGross_max: null, '...': null }
};

const payoutFileListResponse = listResponse({
    aggregates: payoutTotalsAggregates,
    rows: [{
        coreId: 105,
        fileKey: '{uuid}_2025_03_18_13_20_10.xls',
        merchantId: 150,
        dateFrom: '2025-02-03 00:00:00',
        dateTo: '2025-02-09 23:59:59',
        isPaid: 1,
        amountPaid: '-26.603',
        rollingReservePaid: '0.000',
        rollingReserveRetained: '3.103',
        currency: 'EUR',
        updatedAt: '2025-03-18 13:20:31',
        totalNet: '-26.603',
        rollingReserveRetainedPaidInFileId: 0,
        rollingReserveRetainedUntil: '2025-08-02',
        pastRRRTouchedAt: '2025-03-18 13:20:31',
        totalGross: '31.000',
        totalReversed: '0.000',
        totalReversedPaid: '0.000',
        noSuccAfterNull: 4,
        taxNoSuccAfterNull: '8.000',
        sumSuccAfterNull: '31.000',
        '...': '...',
        consumerPayoutTotalFee: null,
        consumerPayoutTotalAmount: null,
        consumerPayoutTotalNumber: null,
        pki: 105
    }],
    sortable: ['coreId', 'fileKey', 'rollingReserveRetainedPaidInFileId', 'isPaid', 'updatedAt', 'merchantId', 'rollingReserveRetainedUntil', 'pastRRRTouchedAt']
});

const payoutSummaryListResponse = listResponse({
    aggregates: payoutTotalsAggregates,
    rows: [{
        coreId: 3,
        fileKey: '{uuid}_2024_05_15_14_22_23.xls',
        merchantId: 150,
        dateFrom: '2024-03-01 00:00:00',
        dateTo: '2024-03-31 23:59:59',
        amountPaid: '-136.20',
        totalNet: '-137.945',
        totalGross: '15.000',
        totalReversed: '0.000',
        totalReversedPaid: '0.000',
        rollingReservePaid: '1.745',
        rollingReserveRetained: '1.575',
        currency: 'EUR',
        createdAt: '2024-05-15 14:22:23',
        updatedAt: '2024-05-15 14:22:25',
        noSuccAfterNull: 5,
        noFundsRefundedAfterNull: 7,
        sumFundsRefundedAfterNull: '51.000',
        noFundsSentAfterNull: 4,
        sumFundsSentAfterNull: '4.000',
        '...': '...',
        consumerPayoutTotalFee: '0.000',
        consumerPayoutTotalAmount: '0.000',
        consumerPayoutTotalNumber: 0,
        pki: 3
    }],
    sortable: ['fileKey', 'dateFrom', 'dateTo', 'amountPaid', 'totalNet', 'totalGross', 'currency', 'createdAt', 'updatedAt', '...']
});

const payoutAccountsListResponse = listResponse({
    aggregates: emptyAggregates,
    rows: [{
        coreId: 8,
        merchantId: 150,
        currency: 'EUR',
        iban: '...',
        swift: '...',
        vat: '',
        createdAt: '2024-05-22 07:57:05',
        updatedAt: '2025-08-19 14:53:24',
        merchantName: 'Merchant Ltd',
        pki: 8
    }],
    filterable: ['coreId', 'merchantId', 'currency', 'iban', 'swift', 'vat', 'createdAt', 'updatedAt', 'merchantName'],
    sortable: ['coreId', 'merchantId', 'iban']
});

const cryptoAccountsListResponse = listResponse({
    aggregates: emptyAggregates,
    rows: [{
        coreId: 1,
        merchantId: 150,
        currency: 'USDT',
        address: '...',
        chain: '...',
        createdAt: '2023-01-19 13:37:51',
        updatedAt: '2023-01-19 13:37:51',
        merchantName: 'Merchant Ltd',
        pki: 1
    }],
    filterable: ['coreId', 'merchantId', 'currency', 'address', 'chain', 'createdAt', 'updatedAt', 'merchantName'],
    sortable: ['coreId', 'merchantId', 'address']
});

const errorCodesListResponse = listResponse({
    aggregates: emptyAggregates,
    rows: [{ apiCode: 241, messageTemplate: 'Transaction pace too high', pki: null }],
    filterable: ['apiCode', 'messageTemplate'],
    sortable: ['apiCode', 'messageTemplate']
});

const transactionDecisionExample = JSON.stringify({
    ResultCode: 0,
    ResultState: true,
    ResultCdcData: { TXID: '...', FLAGS: 'C', ERRMSG: '', STATE_NAME: 'SUCCEEDED', DESCRIPTION: '' },
    ResultMessage: 'SUCCEEDED',
    ResultTransactionId: '...',
    ResultTransactionGuid: '...',
    ResultTrackingMemberCode: '...'
});

const transactionHistoryListResponse = listResponse({
    rows: [
        {
            status: 'SUCCEEDED',
            fundStatus: 'RECEIVED',
            updatedAt: '2025-05-30 14:30:34',
            settlementStatus: 'FUNDSRECEIVED',
            settlementStatusDateTime: '2025-05-30 14:18:54',
            localTime: '2025-05-30 14:15:38',
            transactionDecision: transactionDecisionExample,
            action: 'isNotification',
            historyCreatedAt: '2025-05-30 14:30:34'
        },
        {
            status: 'SUCCEEDED',
            fundStatus: 'MISSING',
            updatedAt: '2025-05-30 14:18:26',
            settlementStatus: 'FUNDSMISSING',
            settlementStatusDateTime: '2025-05-30 14:18:26',
            localTime: '2025-05-30 14:15:38',
            transactionDecision: transactionDecisionExample,
            action: 'other',
            historyCreatedAt: '2025-05-30 14:18:54'
        }
    ]
});

const notificationsOutListResponse = listResponse({
    aggregates: emptyAggregates,
    rows: [{
        notificationUrl: 'https://merchant.com/notifications',
        payload: JSON.stringify({
            txid: '...',
            txguid: '...',
            finalstatus: 'SUCCEEDED',
            fundsstatus: 'WAITING',
            settlement_fundsstatus: null,
            finaltimestamp: { date: '2025-09-12 10:34:01', timezone_type: 2, timezone: 'UTC' },
            direction: 'C',
            sha256hash: '...'
        }),
        receivedResponse: 'OK',
        createdAt: '2025-09-12 10:35:11',
        transactionId: '...',
        transactionGuid: '...',
        pki: ''
    }],
    filterable: ['notificationUrl', 'payload', 'receivedResponse', 'createdAt'],
    sortable: ['notificationUrl', 'payload', 'receivedResponse', 'createdAt']
});

const payoutAggregatesNote = 'The sums, avgs, mins and maxs objects contain one entry per numeric column, and each row also contains every counter (no*), sum (sum*), tax (tax*) and rolling reserve (rrr*) column listed under filterable. They are abbreviated with "..." here.';

export const directApiEndpoints: readonly Endpoint[] = [
    {
        id: 'direct-transactions',
        title: 'Transactions',
        method: 'POST',
        path: '/merchant/api/transactions/l/i/s/t',
        description: 'List, filter, sort and export merchant transactions. children lists the refunds and chargebacks linked to the transaction.',
        responseExamples: [{ title: 'Success response (200)', content: transactionListResponse }]
    },
    {
        id: 'direct-payout-files',
        title: 'Payout files',
        method: 'POST',
        path: '/merchant/api/merchant-payout-files/l/i/s/t',
        description: 'List merchant payout files. Use coreId and fileKey to download a file.',
        responseExamples: [{ title: 'Success response (200)', content: payoutFileListResponse }],
        note: payoutAggregatesNote
    },
    {
        id: 'direct-payout-summary',
        title: 'Payout summary files',
        method: 'POST',
        path: '/merchant/api/merchant-payout-summary-files/l/i/s/t',
        description: 'List merchant payout summary files. Use coreId and fileKey to download a file.',
        responseExamples: [{ title: 'Success response (200)', content: payoutSummaryListResponse }],
        note: payoutAggregatesNote
    },
    {
        id: 'direct-bank-accounts',
        title: 'Payout accounts',
        method: 'POST',
        path: '/merchant/api/payout-accounts/l/i/s/t',
        description: 'List the merchant payout accounts.',
        responseExamples: [{ title: 'Success response (200)', content: payoutAccountsListResponse }]
    },
    {
        id: 'direct-crypto-accounts',
        title: 'Payout crypto accounts',
        method: 'POST',
        path: '/merchant/api/payout-crypto-accounts/l/i/s/t',
        description: 'List the merchant payout crypto accounts.',
        responseExamples: [{ title: 'Success response (200)', content: cryptoAccountsListResponse }]
    },
    {
        id: 'direct-errors',
        title: 'API error message codes',
        method: 'POST',
        path: '/merchant/api/api-error-messages-codes/l/i/s/t',
        description: 'Retrieve API error codes. Do not call this route for each transaction: cache the response and refresh it at most weekly.',
        responseExamples: [{ title: 'Success response (200)', content: errorCodesListResponse }]
    },
    {
        id: 'direct-history',
        title: 'Transaction history',
        method: 'POST',
        path: '/merchant/api/transactions/{coreId}/histories/l/i/s/t',
        description: 'Retrieve the recorded state changes (status, funds status and settlement status) of a transaction.',
        parameters: coreIdParameter,
        responseExamples: [{ title: 'Success response (200)', content: transactionHistoryListResponse }]
    },
    {
        id: 'direct-notifications',
        title: 'Notification history',
        method: 'POST',
        path: '/merchant/api/transactions/{coreId}/notifications-out/l/i/s/t',
        description: 'Retrieve outgoing notification attempts for a transaction: the payload sent to your notification URL and the response your server returned.',
        parameters: coreIdParameter,
        responseExamples: [{ title: 'Success response (200)', content: notificationsOutListResponse }]
    },
    {
        id: 'direct-import-whitelist',
        title: 'Import emails whitelist',
        method: 'POST',
        path: '/merchant/api/emails-whitelist',
        description: 'Import consumer whitelist records from a CSV file. Same endpoint as Upload/import whitelist.',
        parameters: [{ name: 'file', type: 'File', required: true, description: 'Completed whitelist CSV file sent as multipart/form-data.' }],
        responseExamples: [{ title: 'Success response (200)', content: whitelistImportResponse }]
    },
    {
        id: 'direct-emails-whitelist',
        title: 'Emails whitelist',
        method: 'POST',
        path: '/merchant/api/emails-whitelist/l/i/s/t',
        description: 'List whitelist import records. Same endpoint as List whitelist imports.',
        responseExamples: [{ title: 'Success response (200)', content: whitelistListResponse }]
    },
    { id: 'direct-download-payout', title: 'Download payout file', method: 'GET', path: '/merchant/api/merchant-payout-files/{coreId}/files/{key}', description: 'Download a payout file. The response is a streamed XLS file.', parameters: filePathParameters },
    { id: 'direct-download-summary', title: 'Download payout summary file', method: 'GET', path: '/merchant/api/merchant-payout-summary-files/{coreId}/files/{key}', description: 'Download a payout summary file. The response is a streamed XLS file.', parameters: filePathParameters },
    {
        id: 'direct-summary-transactions',
        title: 'Payout summary transactions CSV',
        method: 'GET',
        path: '/merchant/api/merchant-payout-summary-file-csv-transactions/{coreId}',
        description: 'Download payout summary transactions as a streamed CSV file.',
        parameters: coreIdParameter,
        responseExamples: csvResponseExamples(csvTransactionsExample)
    },
    {
        id: 'direct-summary-payouts',
        title: 'Payout summary consumer payouts CSV',
        method: 'GET',
        path: '/merchant/api/merchant-payout-summary-file-csv-consumer-payouts/{coreId}',
        description: 'Download payout summary consumer payouts as a streamed CSV file.',
        parameters: coreIdParameter,
        responseExamples: csvResponseExamples(csvConsumerPayoutsExample)
    },
    {
        id: 'direct-file-transactions',
        title: 'Payout file transactions CSV',
        method: 'GET',
        path: '/merchant/api/merchant-payout-file-csv-transactions/{coreId}',
        description: 'Download payout file transactions as a streamed CSV file.',
        parameters: coreIdParameter,
        responseExamples: csvResponseExamples(csvTransactionsExample)
    },
    {
        id: 'direct-file-summary',
        title: 'Payout file pending settlement transactions CSV',
        method: 'GET',
        path: '/merchant/api/merchant-payout-file-csv-pending-settlement-transactions/{coreId}',
        description: 'Download the transactions of a payout file that are still waiting for settlement, as a streamed CSV file.',
        parameters: coreIdParameter,
        responseExamples: csvResponseExamples(csvPendingSettlementExample)
    },
    {
        id: 'direct-file-payouts',
        title: 'Payout file consumer payouts CSV',
        method: 'GET',
        path: '/merchant/api/merchant-payout-file-csv-consumer-payouts/{coreId}',
        description: 'Download payout file consumer payouts as a streamed CSV file.',
        parameters: coreIdParameter,
        responseExamples: csvResponseExamples(csvConsumerPayoutsExample)
    }
];

export const notificationFields: readonly Parameter[] = [
    { name: 'txid', type: 'String', required: true, description: 'Transaction identifier (ResultTransactionId from the transaction response).' },
    { name: 'txguid', type: 'UUID', required: true, description: 'Transaction GUID (ResultTransactionGuid from the transaction response).' },
    { name: 'finalstatus', type: 'String', required: true, description: 'Current transaction status, for example SUCCEEDED, FAILED, REFUNDED or CHARGE_BACK. Only REFUNDED is final; any other value may still change.' },
    { name: 'fundsstatus', type: 'String', required: false, description: 'Current funds status. May be empty.' },
    { name: 'settlement_fundsstatus', type: 'String', required: false, description: 'Current settlement funds status. May be empty.' },
    { name: 'finaltimestamp[date]', type: 'DateTime', required: true, description: 'Transaction creation date (Y-m-d H:i:s). This value is part of the sha256hash input.' },
    { name: 'finaltimestamp[timezone_type]', type: 'Integer', required: true, description: 'Always 2.' },
    { name: 'finaltimestamp[timezone]', type: 'String', required: true, description: 'Always UTC.' },
    { name: 'direction', type: 'String', required: false, description: 'Transaction direction, for example C.' },
    { name: 'sha256hash', type: 'String', required: true, description: 'Signature used to verify that the notification was sent by Macropay. See Verify the signature below.' }
];

export const notificationPayloadExample = `txid=2396543143
&txguid=2ba4d857-b4e9-48de-90c2-62b88c732593
&finalstatus=SUCCEEDED
&fundsstatus=WAITING
&settlement_fundsstatus=
&finaltimestamp[date]=2025-09-12 10:34:01
&finaltimestamp[timezone_type]=2
&finaltimestamp[timezone]=UTC
&direction=C
&sha256hash=a02c58bd468...`;

export const notificationHashFormula = `sha256hash = sha256(
    sha256(txid + "." + txguid + "." + finaltimestamp[date])
    + "." + notificationSecret
)`;

export const notificationReceiverExample = `<?php

$notificationSecret = 'YOUR-SHARED-KEY'; // Profile → Technical Information

$expectedHash = hash(
    'sha256',
    hash('sha256', $_POST['txid'] . '.' . $_POST['txguid'] . '.' . $_POST['finaltimestamp']['date'])
        . '.' . $notificationSecret
);

if (!hash_equals($expectedHash, $_POST['sha256hash'] ?? '')) {
    // Signature mismatch: confirm the status with POST /v1/getTransactionStatus instead.
}

if (process_notification($_POST)) {
    http_response_code(200);
    exit;
}

http_response_code(500); // Macropay will retry the notification.`;

export const notificationRetrySchedule = [
    [1, 2, 2],
    [2, 8, 10],
    [3, 15, 25],
    [4, 15, 40],
    [5, 15, 55],
    [6, 15, 70],
    [7, 15, 85],
    [8, 15, 100],
    [9, 15, 115],
    [10, 15, 130]
] as const;

export const webhookRedirectFields: readonly Parameter[] = [
    { name: 'TransactionId', type: 'String', required: true, description: 'ResultTransactionId from the transaction response.' },
    { name: 'PaReqId', type: 'String', required: true, description: 'ResultCdcData.TXID from the transaction response.' },
    { name: 'PaReq', type: 'String', required: true, description: 'Encrypted transaction result. Decrypt it with the CPKey and CPSecret from Profile → Technical Information.' }
];

export const webhookPaReqExample = `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultMessage": "SUCCEEDED",
  "ResultTransactionId": "2396543143",
  "ResultTransactionGuid": "2ba4d857-b4e9-48de-90c2-62b88c732593",
  "ResultTrackingMemberCode": "order-011",
  "ResultCdcData": {
    "TXID": "2396543143",
    "DESCRIPTION": "...",
    "ERRMSG": "",
    "FLAGS": "...",
    "STATE_NAME": "SUCCEEDED"
  }
}`;

export const webhookDecryptExample = `<?php

function decryptPaReq(string $paReq, string $cpKey, string $cpSecret): string|false
{
    $key = hash('sha256', $cpKey);
    // AES-256-CBC expects a 16-byte IV.
    $iv = substr(hash('sha256', $cpSecret), 0, 16);

    return openssl_decrypt(base64_decode($paReq), 'AES-256-CBC', $key, 0, $iv);
}

$result = decryptPaReq($_POST['PaReq'], '<CPKEY>', '<CPSECRET>');

// Use $result only to show the consumer a result page.
// Confirm the status with notifications or POST /v1/getTransactionStatus.`;

export const errorMessages = [
    ['User is not in ecosystem', 'The user email has not been whitelisted. The transaction will be rejected.'],
    ['Minimum ticket size mismatch. Minimum ticket size is 10.00', 'The minimum transaction amount is 10 EUR or equivalent.'],
    ['Maximum ticket size mismatch. Maximum ticket size is 1,000.00 EUR or equivalent', 'The maximum transaction amount is 1,000 EUR or equivalent.'],
    ['Test transaction is not allowed on LIVE environment', 'A testchannel request was sent to the livechannel endpoint.'],
    ['Exceeded maximum number of transaction within 24hrs with same profile. Quota is 5', 'The default quota is five succeeded transactions per user, payment method and 24 hours. This may differ based on the commercial agreement.'],
    ['Exceeded maximum total amount with the same profile in 24h. Quota is 5,000.00 EUR', 'The default quota is 5,000 EUR per user, payment method and 24 hours. This may differ based on the commercial agreement.'],
    ['Tracking member code not set or must be unique for 24hs', 'The tracking member code must be provided and unique for 24 hours.'],
    ['This method is not in use for this account', 'The requested payment method is not enabled for the merchant account. Contact support.'],
    ['Transaction pace too high', 'The default velocity limit prevents consecutive succeeded transactions for the same user within 180 seconds. It applies to pending or succeeded transactions and may differ based on the commercial agreement.'],
    ['High risk consumer profile', 'The consumer has been blacklisted. Contact the compliance team for further details.']
] as const;
